import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid2,
  IconButton,
  InputAdornment,
  Stack,
  ButtonGroup,
  Menu,
  Button,
  CircularProgress
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useGetLocations from "../Hooks/useGetLocation";
import useGetPrograms from "../Hooks/useGetPrograms";
import useGetSemester from "../Hooks/useGetSemester";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import useGetSubjects from "../Hooks/useGetSubjects";
import DataStudentGrid from "../Components/DataGrid/DataStudentGrid";
import StudentInfoSession from "../Components/Dialogs/StudentInfoSession";
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadLoader from "../Components/Loaders/FileDownloadLoader";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useGetStudents } from "../Hooks/useGetStudents";

const options = [
  { label: "All", value: "all" },
  { label: "Group by students", value: "group_students" },
];


const StudentData = () => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.role);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0); // default to first option
  const [inputKey, setInputKey] = useState(null);
  const [donwload, setStartDownload] = useState(false);

  const handleClick = (values) => {
    const selectedSort = options[selectedIndex].value;
    requestFile(values, selectedSort);
  };

  const handleMenuItemClick = (index) => {
    setSelectedIndex(index);
    setAnchorEl(null);
    //requestFile(values, options[index].value); // trigger on select
  };

  const handleToggle = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  const [openStudentInfo, setOpenStudentInfo] = useState(false);
  const [assessmentInfo, setAssessmentInfo] = useState([]);
  const [sessionInfo, setSessionInfo] = useState([]);

  const { locations } = useGetLocations(role);
  const { programs } = useGetPrograms(role);
  const { subjects } = useGetSubjects(role);
  const { students, downloading, searchForStudents, getStudentInfo, pollStudentFileDownload, requestStudentFile} = useGetStudents();
  const { semesters, loading: semester_loading, error: semester_error } = useGetSemester(role);



  const validationSchema = Yup.object().shape({
    search_term: Yup.string()
      .nullable()
      .optional(),
    location_id: Yup.number()
      .typeError('Location ID must be a number')
      .integer('Location ID must be an integer')
      .positive('Location ID must be a positive number')
      .nullable()
      .optional(),
    program_id: Yup.number()
      .typeError('Program ID must be a number')
      .integer('Program ID must be an integer')
      .positive('Program ID must be a positive number')
      .nullable()
      .optional(),
    date: Yup.date()
      .typeError('Invalid date format')
      .nullable()
      .optional(),
    date_end: Yup.date()
      .typeError('Invalid date format')
      .nullable()
      .optional(),
    subject_id: Yup.number()
      .nullable()
      .optional(),
    semester_id: Yup.number()
      .nullable()
      .optional(),
    data_type: Yup.string()
    .nullable()
    .optional()
  });

  const handleSearchClick = async (values) => {
      let date_parsed= null;
      let date_end_parsed = null;
      if (values.date) { date_parsed = DateTime.fromJSDate(new Date(values.date)).toSQLDate(); }
      if (values.date_end) { date_end_parsed = DateTime.fromJSDate(new Date(values.date_end)).toSQLDate(); }
      const data = { ...values, date: date_parsed, date_end: date_end_parsed };
      await searchForStudents(data);
  };

  const handleDateChange = async (value, setFieldValue, submitForm) => {
    try {
      setFieldValue('date', value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleDateEndChange = async (value, setFieldValue, submitForm) => {
    try {
      setFieldValue('date_end', value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleLocationChange = async (e, setFieldValue, submitForm) => {
    try {
      setFieldValue('location_id', e.target.value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleProgramChange = async (e, setFieldValue, submitForm) => {
    try {
      setFieldValue('program_id', e.target.value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleSubjectChange = async (e, setFieldValue, submitForm) => {
    try {
      setFieldValue('subject_id', e.target.value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleSemesterChange = async (e, setFieldValue, submitForm) => {
    try {
      setFieldValue('semester_id', e.target.value);
      submitForm();
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const handleDataTypeChange = async (e, setFieldValue, submitForm) => {
    try {
      setFieldValue('data_type', e.target.value);
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  const closeStudentInfo = () => {
    setOpenStudentInfo(false);
    setAssessmentInfo([]);
    setSessionInfo([]);
  };

  const viewSessionInfo = async (id) => {
    const studentInfo = await getStudentInfo(id);
    if (studentInfo) {
        setOpenStudentInfo(true);
        setSessionInfo(studentInfo.data); 
        setAssessmentInfo(studentInfo.assessment_data);
    }
  };

  function download(url) {
    const a = document.createElement("a");
    a.href = url;
    a.download = "download.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  
  useEffect(() => {
    if (!inputKey) {
      return;
    }
    const cleanup = pollStudentFileDownload(inputKey, (url) => {
      download(url);
      setInputKey(null);;
    });
    return () => cleanup;
  }, [inputKey, pollStudentFileDownload])

  const requestFile = async (values) => {
    const key = await requestStudentFile(values, options[selectedIndex].value);
    if ( key ) { setInputKey(key) ;} 
  }

  const [isSearching, setIsSearching] = useState(false);
  const handleClearClick = (setFieldValue) => {
    try {
      setFieldValue('search_term', '');
    }
    catch(error) {
      console.log(error);
      throw new Error(error);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h5" mb={2} sx={{ fontWeight: 600, textAlign: { xs: 'left', md: 'left' } }}
      >
        Student Data
      </Typography>
      <Formik
        initialValues={{
          search_term: '',
          location_id: null,
          program_id: null,
          date: null,
          date_end: null,
          subject_id: null,
          semester_id: null,
          data_type: 'Sessions',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSearchClick(values);
        }}
      >
        {({ errors, touched, values, handleChange, setFieldValue, submitForm }) => (
          <Form>
            <Grid2 container spacing={2} mb={3}>
              {/* Search Field */}
              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <FormControl fullWidth>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search students"
                  name="search_term"
                  value={values.search_term}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form default behavior if needed
                      submitForm();
                    }
                  }}
                  disabled={isSearching}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {values.search_term && !isSearching && (
                          <IconButton
                            size="small"
                            onClick={() => handleClearClick(setFieldValue)}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        {isSearching && (
                          <CircularProgress size={20} />
                        )}
                        {!isSearching && values.search_term && (
                          <IconButton
                            color="primary"
                            onClick={submitForm}
                            edge="end"
                          >
                            <SearchIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  
                />
                </FormControl>
              </Grid2>

              {/* Location Dropdown */}
              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="location-label">Location</InputLabel>
                  <Select
                    labelId="location-label"
                    name="location_id"
                    value={values.location_id}
                    onChange={(e) => handleLocationChange(e, setFieldValue, submitForm)}
                  >
                    <MenuItem value={null}>All</MenuItem>
                    {locations.map((location, index) => (
                      <MenuItem key={index} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              {/* Program Dropdown */}
              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="program-label">Program</InputLabel>
                  <Select
                    labelId="program-label"
                    name="program_id"
                    value={values.program_id}
                    onChange={(e) => handleProgramChange(e, setFieldValue, submitForm)}
                  >
                    <MenuItem value={null}>All</MenuItem>
                    {programs.map((program, index) => (
                      <MenuItem key={index} value={program.id}>
                        {program.program_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              {/* Date Field */}
              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <Stack spacing={1} direction={'row'}>
                  <DatePicker
                    label="Date start"
                    name="date"
                    value={values.date}
                    onChange={(value) => handleDateChange(value, setFieldValue, submitForm)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={touched.date && Boolean(errors.date)}
                        helperText={touched.date && errors.date}
                      />
                    )}
                  />

                  <DatePicker
                    label="Date end"
                    name="date_end"
                    value={values.date_end}
                    onChange={(value) => handleDateEndChange(value, setFieldValue, submitForm)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={touched.date && Boolean(errors.date)}
                        helperText={touched.date && errors.date}
                      />
                    )}
                  />
                </Stack>
              </Grid2>

              {/* Subject Dropdown */}
              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="subject-label">Subject</InputLabel>
                  <Select
                    labelId="subject-label"
                    name="subject"
                    value={values.subject}
                    onChange={(e) => handleSubjectChange(e, setFieldValue, submitForm)}
                  >
                    <MenuItem value={null}>All</MenuItem>
                    {subjects.map((subject, index) => (
                      <MenuItem key={index} value={subject.id}>
                        {subject.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, lg: 3, md: 4 }}>
                <FormControl margin="dense" fullWidth>
                  <InputLabel>Semester</InputLabel>
                  <Select
                    name="semester_id"
                    value={values.semester_id}
                    onChange={(e) => handleSemesterChange(e, setFieldValue, submitForm)}
                    label="Select semester"
                  >
                    {!semester_loading && semesters.map((item, index) => (
                      <MenuItem key={index} value={item.id}>
                        <Stack>
                          <Typography variant='body2'>
                            {item.title}
                          </Typography>
                          <Typography variant='caption'>
                            {item.year}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{xs: 12, lg: 3, md: 4}}>
                <FormControl margin="dense" fullWidth>
                  <InputLabel>Data type</InputLabel>
                  <Select
                    name="data_type"
                    value={values.data_type}
                    onChange={(e) => handleDataTypeChange(e, setFieldValue, submitForm)}
                    label="Select data type"
                  >
                    {['Assessments', 'Sessions'].map((item, index) => (
                      <MenuItem key={index} value={item}>
                        <Stack direction={'row'} spacing={1}>
                          <Typography variant='body2'>
                            {item}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            - For download
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{xs: 12, lg: 3, md: 4 }}>
              <React.Fragment>
                <FormControl fullWidth margin="dense">
                  <ButtonGroup variant="contained">
                    <Button size="small" sx={{ width: '80%' }} onClick={()=> handleClick(values)} startIcon={<DescriptionIcon />}>
                      <Typography fontWeight={'bold'} variant="caption">
                        Download csv: {options[selectedIndex].label}
                      </Typography>
                    </Button>
                    <Button
                      size="small"
                      onClick={handleToggle}
                      aria-controls={anchorEl ? "sort-menu" : undefined}
                      aria-haspopup="true"
                    >
                      <ArrowDropDownIcon />
                    </Button>
                  </ButtonGroup>
                  <Menu
                    id="sort-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    {options.map((option, index) => (
                      <MenuItem
                        key={option.value}
                        selected={index === selectedIndex}
                        onClick={() => handleMenuItemClick(index)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </FormControl>
              </React.Fragment>
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>

      {downloading ? <FileDownloadLoader />: null }

      <DataStudentGrid 
        data={students} 
        viewSessionInfo={viewSessionInfo} />

        
      <StudentInfoSession 
        sessionInfo={sessionInfo} 
        assessmentInfo={assessmentInfo} 
        open={openStudentInfo} 
        close={closeStudentInfo} />
    </Box>
  );
};

export default StudentData;